import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { normalizeInviteError } from './inviteErrors';
import type {
  OrganisationMembershipBulkSyncDto,
  OrganisationInviteDto,
  OrganisationInviteMembershipSyncDto,
  OrganisationRole,
} from '@flowqa/shared';

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const createOrganisationInvite = async (input: {
  organisationId: string;
  email: string;
  role?: OrganisationRole;
}): Promise<OrganisationInviteDto> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<OrganisationInviteDto>(
      `organisations/${input.organisationId}/invites`,
      {
        email: input.email.trim(),
        ...(input.role ? { role: input.role } : {}),
      },
      { token: authToken, retries: 1 },
    );
  } catch (err) {
    throw normalizeInviteError(err);
  }
};

export const queryOrganisationInvites = async (input: {
  organisationId: string;
}): Promise<OrganisationInviteDto[]> => {
  const authToken = ensureToken();
  try {
    const result = await apiClient.get<{ items: OrganisationInviteDto[] }>(
      `organisations/${input.organisationId}/invites`,
      { token: authToken, retries: 1 },
    );
    return result.items;
  } catch (err) {
    throw normalizeInviteError(err);
  }
};

export const revokeOrganisationInvite = async (input: {
  organisationId: string;
  invitationId: string;
}): Promise<OrganisationInviteDto> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<OrganisationInviteDto>(
      `organisations/${input.organisationId}/invites/${input.invitationId}/revoke`,
      undefined,
      { token: authToken, retries: 1 },
    );
  } catch (err) {
    throw normalizeInviteError(err);
  }
};

export const reconcileOrganisationInviteMembership = async (input: {
  organisationId: string;
}): Promise<OrganisationInviteMembershipSyncDto> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<OrganisationInviteMembershipSyncDto>(
      `organisations/${input.organisationId}/invites/reconcile-membership`,
      undefined,
      { token: authToken, retries: 1 },
    );
  } catch (err) {
    throw normalizeInviteError(err);
  }
};

export const syncOrganisationMembers = async (input: {
  organisationId: string;
}): Promise<OrganisationMembershipBulkSyncDto> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<OrganisationMembershipBulkSyncDto>(
      `organisations/${input.organisationId}/invites/sync-members`,
      undefined,
      { token: authToken, retries: 1 },
    );
  } catch (err) {
    throw normalizeInviteError(err);
  }
};
